import { cn } from "@/lib/utils";
import { KIT } from "@/constants/testIds";

/** One plan as a row: small cover, where the reader is, a thin coral bar, and the one action.
 *  Used on its own (a single plan) and inside the grouped Healthy card (one row per style). */
export const PlanRow = ({ program, title, positionLabel, percent = 0, ctaLabel = "Start", started = false, onOpen }) => (
  <div data-testid={KIT.programCard(program.slug)} className="flex items-center gap-4">
    <span className="ps-photo block aspect-square w-[84px] shrink-0 rounded-[20px]">
      {program.cover_image ? (
        <img src={program.cover_image} alt="" loading="lazy" decoding="async" />
      ) : (
        <span className="grid h-full w-full place-items-center bg-brand-teal font-display text-2xl font-bold text-white/85">
          {(title ?? program.title)?.[0]}
        </span>
      )}
    </span>

    <div className="min-w-0 flex-1">
      <h3 className="font-display text-[17px] font-bold leading-snug text-brand-teal sm:text-[19px]">
        {title ?? program.title}
      </h3>
      {positionLabel && (
        <p
          data-testid={`program-position-${program.slug}`}
          className="mt-1 text-[13px] font-bold text-brand-orange"
        >
          {positionLabel}
        </p>
      )}
      <div className="mt-2 h-[5px] w-full overflow-hidden rounded-full bg-[#EEE9DE]">
        <div
          className="h-full rounded-full transition-[width] duration-700"
          style={{ width: `${percent}%`, backgroundColor: "#DD4115" }}
        />
      </div>
    </div>

    <button
      data-testid={KIT.programCardCta(program.slug)}
      onClick={() => onOpen?.(program)}
      className={cn(
        "ps-tap inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full px-5 text-[13.5px] font-bold transition-colors",
        started
          ? "bg-brand-teal text-white hover:bg-brand-tealDeep"
          : "border border-brand-teal/35 bg-white text-brand-teal hover:border-brand-teal"
      )}
    >
      {ctaLabel}
    </button>
  </div>
);

/** A plan card for every plan that is not one of the two Healthy styles. */
export const PlanCard = ({ program, positionLabel, percent = 0, ctaLabel, onOpen, children }) => (
  <article className="ps-card ps-card-pad space-y-4">
    <div className="flex items-start gap-4">
      <span className="ps-photo block aspect-square w-[84px] shrink-0 rounded-[20px]">
        {program.cover_image ? (
          <img src={program.cover_image} alt="" loading="lazy" decoding="async" />
        ) : (
          <span className="grid h-full w-full place-items-center bg-brand-teal font-display text-2xl font-bold text-white/85">
            {program.title?.[0]}
          </span>
        )}
      </span>
      <div className="min-w-0 flex-1" data-testid={KIT.programCard(program.slug)}>
        <h3 className="font-display text-[19px] font-bold leading-snug text-brand-teal sm:text-[21px]">
          {program.title}
        </h3>
        {positionLabel && (
          <p
            data-testid={`program-position-${program.slug}`}
            className="mt-1 text-[13px] font-bold text-brand-orange"
          >
            {positionLabel}
          </p>
        )}
        <div className="mt-2 h-[5px] w-full overflow-hidden rounded-full bg-[#EEE9DE]">
          <div
            className="h-full rounded-full transition-[width] duration-700"
            style={{ width: `${percent}%`, backgroundColor: "#DD4115" }}
          />
        </div>
      </div>
    </div>

    {/* The access lines carry the detail; the action is a compact pill beside them. */}
    <div className="flex items-end justify-between gap-4">
      <div className="min-w-0 flex-1">{children}</div>
      <button
        data-testid={KIT.programCardCta(program.slug)}
        onClick={() => onOpen?.(program)}
        className="ps-tap inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full bg-brand-teal px-5 text-[13.5px] font-bold text-white transition-colors hover:bg-brand-tealDeep"
      >
        {ctaLabel}
      </button>
    </div>
  </article>
);
